import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';

interface InspectionCutawayHotspot {
  id: string;
  title: string;
  x: number;
  y: number;
  shortExplanation: string;
  checks: string[];
  findings: string[];
  whyItMatters: string;
  reportNote: string;
}

interface ThermalRevealHotspot {
  id: string;
  title: string;
  label: string;
  x: number;
  y: number;
  summary: string;
  detail: string;
}

interface SampleFinding {
  id: string;
  tabLabel: string;
  severity: string;
  severityTone: 'repair' | 'safety' | 'moisture' | 'evaluation';
  category: string;
  title: string;
  location: string;
  explanation: string;
  recommendation: string;
  image: string;
  imageAlt: string;
  imagePosition: string;
  annotationX: number;
  annotationY: number;
  annotationSize: number;
  metadata: string[];
  photoCredit: string;
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements AfterViewInit, OnDestroy {
  @ViewChild('sampleFindingSection') private sampleFindingSection?: ElementRef<HTMLElement>;
  @ViewChild('cutawayModal') private cutawayModal?: ElementRef<HTMLElement>;

  selectedNeedIndex = 0;
  selectedFocusIndex = 0;
  selectedAreaIndex = 0;
  selectedReportIndex = 0;
  selectedCutawayIndex = 0;
  isCutawayModalOpen = false;
  thermalRevealValue = 92;
  selectedThermalHotspotIndex: number | null = null;
  selectedSampleFindingIndex = 0;
  outgoingSampleFindingIndex: number | null = null;
  sampleFindingPageDirection: 'next' | 'previous' = 'next';
  sampleFindingPageSwapping = false;
  sampleFindingOpened = false;

  private sampleFindingObserver?: IntersectionObserver;
  private sampleFindingSwapTimer?: ReturnType<typeof setTimeout>;

  ngAfterViewInit(): void {
    const section = this.sampleFindingSection?.nativeElement;

    if (!section || typeof IntersectionObserver === 'undefined') {
      this.sampleFindingOpened = true;
      return;
    }

    this.sampleFindingObserver = new IntersectionObserver(
      (entries) => {
        this.sampleFindingOpened = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.16);
      },
      {
        rootMargin: '-8% 0px -16% 0px',
        threshold: [0, 0.16, 0.28]
      }
    );
    this.sampleFindingObserver.observe(section);
  }

  ngOnDestroy(): void {
    this.sampleFindingObserver?.disconnect();
    if (this.sampleFindingSwapTimer) {
      clearTimeout(this.sampleFindingSwapTimer);
    }
  }

  private readonly fieldJournalImageMap: Record<string, string> = {
    'assets/images/kari_and_roland_rr.jpg': 'assets/images/design-a/kari-roland-georgetown-field-journal.jpg',
    'assets/images/kari_roland_door.jpg': 'assets/images/design-a/kari-roland-door-field-journal.jpg',
    'assets/images/roland_bridge_standing.jpg': 'assets/images/design-a/roland-bridge-standing-field-journal.jpg',
    'assets/images/kari_bridge.jpg': 'assets/images/design-a/kari-bridge-field-journal.jpg'
  };



  fieldStats = [
    {
      kicker: 'Systems',
      value: '7',
      label: 'visible home systems'
    },
    {
      kicker: 'Checkup notes',
      value: '52',
      label: 'visible checkpoints'
    },
    {
      kicker: 'Base',
      value: 'GTX',
      label: 'Georgetown care'
    },
    {
      kicker: 'License',
      value: '23635',
      label: 'TREC professional inspector'
    }
  ];

  sampleFindings: SampleFinding[] = [
    {
      id: 'roofing',
      tabLabel: 'Roofing',
      severity: 'Repair Recommended',
      severityTone: 'repair',
      category: 'Roofing',
      title: 'Deteriorated Pipe Boot Flashing',
      location: 'Rear roof slope at plumbing vent',
      explanation: 'The boot flashing around the roof penetration shows deterioration at the seal area. This condition can allow rainwater to enter around the pipe and stain roof decking or ceiling materials below.',
      recommendation: 'Have a qualified roofing contractor replace the pipe boot flashing and check the nearby shingles, fasteners, and attic decking for moisture staining.',
      image: 'assets/images/sample-findings/roof-pipe-boot-flashing.jpg',
      imageAlt: 'Real defect photo showing damaged flashing around an old roof vent penetration',
      imagePosition: 'center center',
      annotationX: 46,
      annotationY: 42,
      annotationSize: 29,
      metadata: ['Photo documented', 'Visual inspection', 'Contractor recommended'],
      photoCredit: 'Photo: deanj, CC BY 2.0'
    },
    {
      id: 'electrical',
      tabLabel: 'Electrical',
      severity: 'Safety Concern',
      severityTone: 'safety',
      category: 'Electrical',
      title: 'Double-Tapped Breaker',
      location: 'Main service panel in garage',
      explanation: 'Two conductors appear to be connected at one breaker terminal. Unless the breaker is specifically listed for more than one conductor, this can create a loose connection, overheating, or arcing.',
      recommendation: 'Have a licensed electrician evaluate the panel and correct the connection using an approved breaker, pigtail, or other code-compliant repair.',
      image: 'assets/images/sample-findings/electrical-double-tapped-breaker.jpg',
      imageAlt: 'Real open electrical panel photo with breakers and visible conductors',
      imagePosition: 'center 42%',
      annotationX: 35,
      annotationY: 57,
      annotationSize: 22,
      metadata: ['Photo documented', 'Visual inspection', 'Contractor recommended'],
      photoCredit: 'Photo: Home Improvement Stack Exchange user contribution, CC BY-SA'
    },
    {
      id: 'plumbing',
      tabLabel: 'Plumbing',
      severity: 'Active Leak',
      severityTone: 'moisture',
      category: 'Plumbing',
      title: 'Active Leak at Drain Assembly',
      location: 'Hall bathroom vanity drain',
      explanation: 'Water was observed dripping from the drain assembly while the fixture was operated. Active leaks can damage cabinet materials and may conceal additional moisture below the fixture.',
      recommendation: 'Have a qualified plumber repair the drain assembly, dry affected cabinet surfaces, and monitor the area after repair to confirm the leak has stopped.',
      image: 'assets/images/sample-findings/plumbing-drain-leak.jpg',
      imageAlt: 'Real photo of water dripping from a leaking sink P-trap drain assembly',
      imagePosition: '35% center',
      annotationX: 30,
      annotationY: 68,
      annotationSize: 18,
      metadata: ['Photo documented', 'Visual inspection', 'Contractor recommended'],
      photoCredit: 'Photo: Tony Webster, CC BY 2.0'
    },
    {
      id: 'foundation',
      tabLabel: 'Foundation',
      severity: 'Further Evaluation',
      severityTone: 'evaluation',
      category: 'Foundation',
      title: 'Stair-Step Cracking at Masonry',
      location: 'Right exterior masonry near front corner',
      explanation: 'A stair-step crack follows the mortar joints in the masonry. This pattern can be associated with movement and should be considered with door, window, drainage, and floor performance clues.',
      recommendation: 'Have a qualified foundation or structural professional evaluate the crack, determine whether movement is active, and recommend appropriate monitoring or repair.',
      image: 'assets/images/sample-findings/foundation-stair-step-crack.jpg',
      imageAlt: 'Real masonry wall photo with stair-step cracking through brick mortar joints',
      imagePosition: 'center center',
      annotationX: 49,
      annotationY: 54,
      annotationSize: 33,
      metadata: ['Photo documented', 'Visual inspection', 'Contractor recommended'],
      photoCredit: 'Photo: Gerard Dukker / RCE, CC BY-SA 4.0'
    }
  ];

  serviceAreas = [
    'Georgetown',
    'Round Rock',
    'Pflugerville',
    'Hutto',
    'Taylor',
    'Manor',
    'Elgin',
    'Cedar Park',
    'Liberty Hill',
    'Jarrell',
    'Salado',
    'Belton',
    'Temple',
    'Buda',
    'Kyle',
    'Austin'
  ];

  areaGuides = [
    {
      city: 'Georgetown',
      corridor: 'Home base',
      routeLabel: 'GTX base plan',
      inspectionCue: 'Established homes, new builds, and option-period timelines often need clear visible-system notes.',
      mapX: 47,
      mapY: 30,
      title: 'Home-base inspection support for established streets, new builds, and fast-moving moves.',
      description: 'GTX helps Georgetown clients understand what was visible on inspection day and what deserves follow-up before the next home decision.',
      checkpoints: ['Local scheduling support', 'New-build and resale context', 'Fast follow-up before deadlines']
    },
    {
      city: 'Round Rock',
      corridor: 'South plan',
      routeLabel: 'Austin metro edge',
      inspectionCue: 'Fast-moving transactions benefit from a report that is organized by system and easy to discuss.',
      mapX: 58,
      mapY: 46,
      title: 'A clear plan for fast-moving Round Rock inspection timelines.',
      description: 'A documented walk-through and report help clients move quickly without losing sight of important home details.',
      checkpoints: ['Option-period timing', 'Clear report priorities', 'Agent-friendly documentation']
    },
    {
      city: 'Liberty Hill',
      corridor: 'West growth plan',
      routeLabel: 'New construction corridor',
      inspectionCue: 'Growth-area and new-construction homes need documentation around current phase, warranty, and builder timing.',
      mapX: 27,
      mapY: 24,
      title: 'Growth-area homes still need plain-language inspection markers.',
      description: 'Rapid growth and new construction make phase inspections, warranty checks, and clear explanations especially valuable.',
      checkpoints: ['Phase inspection context', 'Builder deadline notes', 'Warranty planning']
    },
    {
      city: 'Austin',
      corridor: 'Urban south plan',
      routeLabel: 'Central Texas mixed-stock plan',
      inspectionCue: 'Older systems, remodels, and fast transactions benefit from practical notes that separate major issues from maintenance.',
      mapX: 67,
      mapY: 72,
      title: 'Central Texas homes need practical property context.',
      description: 'From older systems to newer renovations, GTX keeps the inspection focused on visible conditions and useful next steps.',
      checkpoints: ['Older-home context', 'Renovation clues', 'Maintenance planning']
    }
  ];

  needs = [
    {
      label: 'Buying',
      title: 'What should I understand before the option period ends?',
      description: 'A complete inspection gives you an unbiased report, clear priorities, and a walk-through with Roland so you understand the condition of the home before the next contract decision.',
      deadline: 'Option period',
      conciergeCue: 'Share the deadline first so GTX can align scheduling, access, and report timing around the checkup.',
      image: 'assets/images/home-home.jpg',
      imagePosition: 'center center',
      keyQuestion: 'Which visible issues should shape the option-period conversation?',
      reportFocus: 'Safety, function, and repair-priority context',
      planSteps: [
        'Send the address, deadline, and access notes.',
        'Walk through visible systems with Roland.',
        'Use the report to discuss next steps with your agent.'
      ],
      send: [
        'Property address and access details',
        'Option-period deadline or agent timing',
        'Seller disclosure concerns or walkthrough notes'
      ]
    },
    {
      label: 'Selling',
      title: 'What should I understand before listing?',
      description: 'A pre-listing inspection can help you understand visible concerns before negotiations begin and keep the selling process more informed.',
      deadline: 'Before listing',
      conciergeCue: 'Use the inspection checkup to understand visible conditions before photos, showings, and buyer negotiations begin.',
      image: 'assets/images/inspections-interior.jpeg',
      imagePosition: 'center center',
      keyQuestion: 'What should be understood before buyers begin asking questions?',
      reportFocus: 'Pre-listing clarity and visible-condition documentation',
      planSteps: [
        'Share the listing window and known repairs.',
        'Document visible concerns before showings.',
        'Plan repairs or disclosures with better context.'
      ],
      send: [
        'Target listing window',
        'Known updates, repairs, or recurring concerns',
        'Preferred contact path for scheduling follow-up'
      ]
    },
    {
      label: 'New Construction',
      title: 'What should I document before the builder deadline?',
      description: 'GTX offers newly constructed and frame-to-finish packages with multiple inspection points and re-inspections through the first year.',
      deadline: 'Builder milestone',
      conciergeCue: 'Builder timelines move quickly, so GTX can align the checkup around the current construction phase.',
      image: 'assets/images/new-home-construction.jpg',
      imagePosition: 'center center',
      keyQuestion: 'What should be documented before the next builder deadline?',
      reportFocus: 'Phase, final walk-through, and warranty timing',
      planSteps: [
        'Name the builder milestone or warranty date.',
        'Coordinate access before the phase closes.',
        'Use the report for builder follow-up.'
      ],
      send: [
        'Current construction phase',
        'Builder walkthrough or warranty date',
        'Access instructions for the property'
      ]
    },
    {
      label: 'Maintenance',
      title: 'What should I prioritize in the home I already own?',
      description: 'A maintenance inspection helps identify visible deferred maintenance concerns before they become more expensive problems.',
      deadline: 'Planning window',
      conciergeCue: 'A maintenance checkup helps organize visible concerns before seasonal work or repair planning.',
      image: 'assets/images/inspections-exterior.jpg',
      imagePosition: 'center bottom',
      keyQuestion: 'Which visible maintenance items should be prioritized first?',
      reportFocus: 'Deferred maintenance and practical homeowner planning',
      planSteps: [
        'Send the concern, photos, or seasonal goal.',
        'Review visible systems with maintenance context.',
        'Turn the report into a practical upkeep list.'
      ],
      send: [
        'Main concern or maintenance goal',
        'Photos or notes if you already have them',
        'Ideal inspection window'
      ]
    }
  ];

  benefits = [
    {
      title: 'Plain-language inspection report',
      description: 'Concerns are documented clearly so you and your agent can follow the inspection plan without translating technical language alone.'
    },
    {
      title: 'Calm on-site explanation',
      description: 'Roland takes time to explain visible conditions so clients leave with context, not just a list.'
    },
    {
      title: 'Thermal imaging and RecallChek',
      description: 'Helpful inspection tools add another layer of context after the standard visible review.'
    },
    {
      title: 'Local resource routing',
      description: 'GTX can connect clients with Central Texas resources when informational referrals are needed.'
    }
  ];

  thermalRevealHotspots: ThermalRevealHotspot[] = [
    {
      id: 'moisture',
      title: 'Possible moisture pattern',
      label: 'Moisture',
      x: 35,
      y: 61,
      summary: 'Cooler surface areas near a wall or baseboard can point to possible moisture.',
      detail: 'Thermal imaging helps guide follow-up checks with a moisture meter and visual review. A temperature difference alone does not confirm hidden damage.'
    },
    {
      id: 'insulation',
      title: 'Insulation gap clue',
      label: 'Insulation',
      x: 55,
      y: 31,
      summary: 'Uneven temperature bands can suggest missing or compressed insulation.',
      detail: 'The image helps the inspector decide where further attic or wall-adjacent evaluation may be useful, without claiming to see inside the wall.'
    },
    {
      id: 'air-leakage',
      title: 'Air leakage clue',
      label: 'Air leak',
      x: 78,
      y: 48,
      summary: 'Temperature outlines around windows or trim can suggest air movement.',
      detail: 'This can help guide closer review of seals, weatherstripping, and comfort concerns during the visible inspection.'
    }
  ];

  reportPreviewTabs = [
    {
      label: 'Brief',
      metric: 'Condition summary',
      title: 'Start with the home condition summary before the details.',
      image: 'assets/images/home-home.jpg',
      imagePosition: 'center center',
      description: 'The report should help clients and agents quickly understand what was observed, what deserves attention, and where to look next.',
      fieldSignal: 'Overall home snapshot',
      clientQuestion: 'What deserves attention first?',
      nextAction: 'Review the summary with your agent',
      bullets: [
        'Plain-language overview of visible conditions',
        'Helpful context for the buyer, seller, or homeowner',
        'A calmer starting point for the agent conversation'
      ]
    },
    {
      label: 'Evidence',
      metric: 'Photo-supported notes',
      title: 'Visible concerns are documented with practical context.',
      image: 'assets/images/inspections-electrical.jpeg',
      imagePosition: 'center center',
      description: 'GTX documents visible concerns so clients can see the condition, understand the location, and ask better follow-up questions.',
      fieldSignal: 'System photo and location',
      clientQuestion: 'Where was the condition observed?',
      nextAction: 'Ask qualified pros about scope',
      bullets: [
        'Clear photos from inspected areas',
        'System-by-system organization',
        'Notes written for client understanding'
      ]
    },
    {
      label: 'Priorities',
      metric: 'Decision priorities',
      title: 'The report helps clients sort what needs attention.',
      image: 'assets/images/inspections-attic.jpeg',
      imagePosition: 'center center',
      description: 'The goal is not to overwhelm clients with a long list. The goal is to help them understand what matters for their next decision.',
      fieldSignal: 'Safety, function, maintenance',
      clientQuestion: 'Which items should shape the next decision?',
      nextAction: 'Separate repair scope from upkeep',
      bullets: [
        'Safety and functional concerns are easier to spot',
        'Maintenance context is separated from major issues',
        'Clients can discuss repair scope with qualified pros'
      ]
    },
    {
      label: 'Follow-up',
      metric: 'After-report support',
      title: 'Questions after the walk-through still have a path.',
      image: 'assets/images/kari_headshot.jpg',
      imagePosition: 'center top',
      description: 'GTX keeps communication approachable after the appointment so scheduling, report access, and follow-up questions stay organized.',
      fieldSignal: 'Scheduling and communication',
      clientQuestion: 'What happens if I have a follow-up question?',
      nextAction: 'Call, text, or email GTX',
      bullets: [
        'Friendly scheduling and communication support',
        'Central Texas resource list when helpful',
        'Privacy-first handling of form and contact details'
      ]
    }
  ];

  readinessSteps = [
    {
      title: 'Property address',
      description: 'Have the address, gate codes, and access notes ready for scheduling.',
      checked: true
    },
    {
      title: 'Inspection goal',
      description: 'Share whether you are buying, selling, building, or planning maintenance.',
      checked: false
    },
    {
      title: 'Preferred timing',
      description: 'Let GTX know the option period, builder deadline, or ideal inspection window.',
      checked: false
    },
    {
      title: 'Questions or concerns',
      description: 'Send any known areas you want Roland to pay close attention to during the walk-through.',
      checked: false
    }
  ];

  focusAreas = [
    {
      label: 'Roof',
      title: 'Roof and drainage details',
      image: 'assets/images/inspections-roof.png',
      imagePosition: 'center top',
      note: 'Shingles, flashings, vents, gutters, drip edges, skylights, and other visible roof-related items are reviewed.',
      metric: '9 visible roof checkpoints'
    },
    {
      label: 'Electrical',
      title: 'Panel and wiring review',
      image: 'assets/images/inspections-electrical.jpeg',
      imagePosition: 'center center',
      note: 'Service entries, panels, circuits, receptacles, fixtures, visible wiring, grounding, and bonding systems are checked.',
      metric: '8 electrical checkpoints'
    },
    {
      label: 'HVAC',
      title: 'Comfort system context',
      image: 'assets/images/inspections-hvac.jpeg',
      imagePosition: 'center center',
      note: 'A/C condensers, furnaces, filters, ductwork, and system differential readings help clarify visible HVAC condition.',
      metric: '6 HVAC checkpoints'
    },
    {
      label: 'Plumbing',
      title: 'Water and fixture review',
      image: 'assets/images/inspections-plumbing.jpeg',
      imagePosition: '18% center',
      note: 'Water meter, cutoff, pressure, hose bibs, sinks, baths, showers, toilets, traps, and visible plumbing are reviewed.',
      metric: '7 plumbing checkpoints'
    }
  ];

  inspectionCutawayHotspots: InspectionCutawayHotspot[] = [
    {
      id: 'roof',
      title: 'Roof',
      x: 50,
      y: 17,
      shortExplanation: 'Roof surfaces, penetrations, and visible drainage details show how the home is shedding water.',
      checks: ['Roof coverings', 'Flashing and penetrations', 'Gutters and downspouts', 'Visible roof drainage paths'],
      findings: ['Damaged or lifted shingles', 'Exposed fasteners', 'Deteriorated sealant', 'Debris in gutters'],
      whyItMatters: 'Roof conditions can affect interior moisture, attic performance, and repair timing. Clear documentation helps clients understand whether a roof concern is maintenance, repair, or a qualified specialist follow-up.',
      reportNote: 'Observed damaged shingles and exposed fasteners at the rear roof slope. Recommend evaluation and repair by a qualified roofing contractor.'
    },
    {
      id: 'attic',
      title: 'Attic',
      x: 50,
      y: 29,
      shortExplanation: 'The attic helps reveal ventilation, insulation, moisture clues, and visible roof framing conditions.',
      checks: ['Accessible attic framing', 'Ventilation openings', 'Insulation depth and coverage', 'Visible moisture staining'],
      findings: ['Compressed or missing insulation', 'Blocked ventilation', 'Rodent activity clues', 'Staining around roof penetrations'],
      whyItMatters: 'Attic conditions influence comfort, energy use, and how the roof assembly performs during Central Texas heat and storms.',
      reportNote: 'Insulation was missing in multiple attic areas. Recommend correction to improve thermal performance and reduce comfort concerns.'
    },
    {
      id: 'hvac',
      title: 'HVAC',
      x: 88,
      y: 72,
      shortExplanation: 'Visible heating, cooling, filters, ducts, and condensate components are reviewed for condition and operation clues.',
      checks: ['Indoor equipment', 'Outdoor condenser', 'Filters and registers', 'Visible ductwork and condensate routing'],
      findings: ['Dirty filters', 'Damaged duct insulation', 'Condensate drain concerns', 'Aged or unserviced equipment'],
      whyItMatters: 'HVAC issues affect comfort, operating cost, and follow-up planning. GTX notes visible clues so clients can discuss service history and repair scope with a qualified HVAC professional.',
      reportNote: 'The return air filter was heavily soiled at the time of inspection. Recommend replacement and routine service by a qualified HVAC technician.'
    },
    {
      id: 'electrical-panel',
      title: 'Electrical Panel',
      x: 27,
      y: 61,
      shortExplanation: 'The panel review focuses on visible safety, labeling, conductor condition, and accessible service equipment.',
      checks: ['Main panel enclosure', 'Breakers and labeling', 'Visible conductors', 'Grounding and bonding clues'],
      findings: ['Missing labels', 'Open knockouts', 'Double-tapped conductors', 'Improper breaker or conductor conditions'],
      whyItMatters: 'Electrical deficiencies can create safety and reliability concerns. Clear panel notes help clients know when a licensed electrician should evaluate further.',
      reportNote: 'Observed multiple unlabeled breakers in the service panel. Recommend accurate circuit labeling for safety and service clarity.'
    },
    {
      id: 'plumbing',
      title: 'Plumbing',
      x: 59,
      y: 66,
      shortExplanation: 'Visible supply, drain, fixtures, shutoffs, and water pressure clues are documented during the inspection.',
      checks: ['Fixture operation', 'Visible supply piping', 'Drainage and traps', 'Shutoffs and hose bibs'],
      findings: ['Active or prior leaks', 'Loose fixtures', 'Slow drains', 'Corrosion or damaged supply lines'],
      whyItMatters: 'Plumbing concerns can lead to moisture damage, fixture failures, and unexpected repair costs if they are not understood early.',
      reportNote: 'Moisture staining was present below the hall bathroom sink trap. Recommend repair by a qualified plumber and monitoring after correction.'
    },
    {
      id: 'water-heater',
      title: 'Water Heater',
      x: 70,
      y: 66,
      shortExplanation: 'Water heater age, installation, safety components, and visible leakage clues are reviewed where accessible.',
      checks: ['Temperature-pressure relief valve', 'Drain pan and drain line', 'Visible venting or electrical connections', 'Leakage and corrosion clues'],
      findings: ['Missing drain pan', 'Improper relief valve discharge', 'Corrosion at fittings', 'Aged unit near service life concerns'],
      whyItMatters: 'Water heater installation details can affect safety, leakage risk, and replacement planning.',
      reportNote: 'The water heater temperature-pressure relief valve discharge line terminated improperly. Recommend correction by a qualified plumber.'
    },
    {
      id: 'foundation',
      title: 'Foundation',
      x: 50,
      y: 80,
      shortExplanation: 'Visible foundation edges, floor performance clues, and exterior conditions are reviewed for signs of movement or distress.',
      checks: ['Visible slab or pier areas', 'Cracking and separation', 'Interior floor clues', 'Moisture near foundation perimeter'],
      findings: ['Cracks at visible edges', 'Door or window movement clues', 'Soil contact concerns', 'Moisture accumulation near the slab'],
      whyItMatters: 'Foundation observations help clients understand visible structural clues and when a qualified foundation specialist may be needed.',
      reportNote: 'Visible cracking was present at the front foundation edge. Recommend monitoring and further evaluation if movement or moisture concerns continue.'
    },
    {
      id: 'crawlspace',
      title: 'Crawlspace',
      x: 38,
      y: 81,
      shortExplanation: 'Accessible crawlspace areas are checked for visible structure, moisture, ventilation, and clearance conditions.',
      checks: ['Accessible framing', 'Ventilation and vapor barrier clues', 'Moisture evidence', 'Plumbing and wiring visibility'],
      findings: ['Standing water', 'Damaged or deteriorated framing', 'Low clearance', 'Missing vapor barrier areas'],
      whyItMatters: 'Crawlspace conditions can affect structure, indoor air quality, pests, and moisture control.',
      reportNote: 'Moist soil and debris were observed in the accessible crawlspace area. Recommend correction of drainage conditions and cleanup.'
    },
    {
      id: 'grading-drainage',
      title: 'Grading/Drainage',
      x: 14,
      y: 88,
      shortExplanation: 'Exterior grading, downspout discharge, and surface water paths are reviewed around the home.',
      checks: ['Soil slope near foundation', 'Downspout discharge', 'Low areas near exterior walls', 'Walkways and hardscape drainage'],
      findings: ['Negative grading', 'Short downspout extensions', 'Ponding water clues', 'Erosion near the foundation'],
      whyItMatters: 'Water management is one of the most important exterior maintenance items for Central Texas homes.',
      reportNote: 'Soil sloped toward the foundation at the left exterior wall. Recommend improving grading to direct water away from the structure.'
    },
    {
      id: 'windows-doors',
      title: 'Windows/Doors',
      x: 53,
      y: 62,
      shortExplanation: 'Accessible windows and doors are checked for operation, sealing, damage, and safety-related conditions.',
      checks: ['Representative window operation', 'Door fit and latch operation', 'Weatherstripping and sealant', 'Glazing and frame condition'],
      findings: ['Failed or fogged glazing', 'Damaged screens', 'Loose locks or latches', 'Gaps that allow air or water intrusion'],
      whyItMatters: 'Windows and doors affect comfort, water control, security, and everyday use of the home.',
      reportNote: 'The rear bedroom window showed fogging between glass panes. Recommend evaluation and repair by a qualified window contractor.'
    },
    {
      id: 'exterior-siding',
      title: 'Exterior Siding',
      x: 21,
      y: 48,
      shortExplanation: 'Exterior wall cladding, trim, clearances, penetrations, and visible deterioration are documented.',
      checks: ['Siding and trim condition', 'Clearance at grade and roof lines', 'Sealant at penetrations', 'Paint and moisture protection'],
      findings: ['Wood rot or swelling', 'Cracked sealant', 'Siding too close to grade', 'Missing or damaged trim pieces'],
      whyItMatters: 'Exterior siding is part of the water-control layer. Small maintenance gaps can become larger moisture problems over time.',
      reportNote: 'Deteriorated trim was observed at the right exterior window. Recommend repair and sealing to help limit moisture intrusion.'
    },
    {
      id: 'garage-safety',
      title: 'Garage Safety',
      x: 72,
      y: 62,
      shortExplanation: 'Garage doors, openers, fire separation, steps, and accessible safety features are reviewed.',
      checks: ['Auto-reverse features', 'Door opener condition', 'Fire separation clues', 'Steps and handrails'],
      findings: ['Missing self-closing door hardware', 'Auto-reverse not responding', 'Damaged fire-rated surfaces', 'Trip hazards at steps'],
      whyItMatters: 'Garage safety items can affect fire separation, vehicle access, and everyday household safety.',
      reportNote: 'The garage vehicle door opener did not reverse when tested with resistance. Recommend adjustment or repair by a qualified garage door technician.'
    }
  ];

  selectNeed(index: number): void {
    this.selectedNeedIndex = index;
  }

  get selectedNeed() {
    return this.needs[this.selectedNeedIndex];
  }

  selectFocus(index: number): void {
    this.selectedFocusIndex = index;
  }

  get selectedFocus() {
    return this.focusAreas[this.selectedFocusIndex];
  }

  selectSampleFinding(index: number, direction?: 'next' | 'previous'): void {
    const nextIndex = (index + this.sampleFindings.length) % this.sampleFindings.length;

    if (nextIndex === this.selectedSampleFindingIndex && !this.sampleFindingPageSwapping) {
      return;
    }

    if (this.sampleFindingSwapTimer) {
      clearTimeout(this.sampleFindingSwapTimer);
    }

    this.outgoingSampleFindingIndex = this.selectedSampleFindingIndex;
    this.sampleFindingPageDirection = direction || this.resolveSampleFindingDirection(nextIndex);
    this.sampleFindingPageSwapping = true;
    this.selectedSampleFindingIndex = nextIndex;

    this.sampleFindingSwapTimer = setTimeout(() => {
      this.sampleFindingPageSwapping = false;
      this.outgoingSampleFindingIndex = null;
    }, 620);
  }

  get selectedSampleFinding(): SampleFinding {
    return this.sampleFindings[this.selectedSampleFindingIndex];
  }

  get outgoingSampleFinding(): SampleFinding | null {
    if (this.outgoingSampleFindingIndex === null) {
      return null;
    }

    return this.sampleFindings[this.outgoingSampleFindingIndex];
  }

  get selectedSampleFindingNumber(): string {
    return this.sampleFindingNumber(this.selectedSampleFindingIndex);
  }

  get outgoingSampleFindingNumber(): string {
    return this.outgoingSampleFindingIndex === null ? '' : this.sampleFindingNumber(this.outgoingSampleFindingIndex);
  }

  get sampleFindingTotal(): string {
    return String(this.sampleFindings.length).padStart(2, '0');
  }

  nextSampleFinding(): void {
    this.selectSampleFinding(this.selectedSampleFindingIndex + 1, 'next');
  }

  previousSampleFinding(): void {
    this.selectSampleFinding(this.selectedSampleFindingIndex - 1, 'previous');
  }

  private sampleFindingNumber(index: number): string {
    return String(index + 1).padStart(2, '0');
  }

  private resolveSampleFindingDirection(nextIndex: number): 'next' | 'previous' {
    const forwardDistance = (nextIndex - this.selectedSampleFindingIndex + this.sampleFindings.length) % this.sampleFindings.length;
    const backwardDistance = (this.selectedSampleFindingIndex - nextIndex + this.sampleFindings.length) % this.sampleFindings.length;

    return forwardDistance <= backwardDistance ? 'next' : 'previous';
  }

  selectCutawayHotspot(index: number): void {
    this.selectedCutawayIndex = this.normalizeCutawayIndex(index);
  }

  openCutawayModal(index: number): void {
    this.selectCutawayHotspot(index);
    this.isCutawayModalOpen = true;
  }

  closeCutawayModal(): void {
    this.isCutawayModalOpen = false;
  }

  selectPreviousCutawayHotspot(): void {
    this.selectCutawayHotspot(this.selectedCutawayIndex - 1);
    this.resetCutawayModalScroll();
  }

  selectNextCutawayHotspot(): void {
    this.selectCutawayHotspot(this.selectedCutawayIndex + 1);
    this.resetCutawayModalScroll();
  }

  get cutawaySystemCount(): number {
    return this.inspectionCutawayHotspots.length;
  }

  get selectedCutawayNumber(): string {
    return String(this.selectedCutawayIndex + 1).padStart(2, '0');
  }

  get cutawaySystemTotal(): string {
    return String(this.cutawaySystemCount).padStart(2, '0');
  }

  @HostListener('document:keydown.escape')
  closeCutawayModalFromEscape(): void {
    if (this.isCutawayModalOpen) {
      this.closeCutawayModal();
    }
  }

  get selectedCutawayHotspot(): InspectionCutawayHotspot {
    return this.inspectionCutawayHotspots[this.selectedCutawayIndex];
  }

  private normalizeCutawayIndex(index: number): number {
    return (index + this.inspectionCutawayHotspots.length) % this.inspectionCutawayHotspots.length;
  }

  private resetCutawayModalScroll(): void {
    setTimeout(() => {
      this.cutawayModal?.nativeElement.scrollTo({ top: 0, left: 0 });
    });
  }

  get thermalClipPath(): string {
    return `inset(0 ${100 - this.thermalRevealValue}% 0 0)`;
  }

  get selectedThermalHotspot(): ThermalRevealHotspot | null {
    if (this.selectedThermalHotspotIndex === null) {
      return null;
    }

    return this.thermalRevealHotspots[this.selectedThermalHotspotIndex] || null;
  }

  get thermalRevealText(): string {
    return `${this.thermalRevealValue}% Thermal View visible`;
  }

  get thermalPopoverLeft(): number {
    const hotspot = this.selectedThermalHotspot;
    if (!hotspot) {
      return 50;
    }

    return Math.min(74, Math.max(8, hotspot.x + (hotspot.x > 62 ? -30 : 5)));
  }

  get thermalPopoverTop(): number {
    const hotspot = this.selectedThermalHotspot;
    if (!hotspot) {
      return 50;
    }

    return Math.min(68, Math.max(10, hotspot.y + 7));
  }

  setThermalReveal(value: string | number): void {
    const nextValue = Number(value);

    if (Number.isNaN(nextValue)) {
      return;
    }

    this.thermalRevealValue = Math.min(92, Math.max(8, nextValue));
  }

  onThermalSliderKeydown(event: KeyboardEvent): void {
    const keySteps: Record<string, number> = {
      ArrowLeft: -2,
      ArrowDown: -2,
      ArrowRight: 2,
      ArrowUp: 2,
      PageDown: -10,
      PageUp: 10
    };
    let nextValue = this.thermalRevealValue;

    if (event.key in keySteps) {
      nextValue += keySteps[event.key];
    } else if (event.key === 'Home') {
      nextValue = 8;
    } else if (event.key === 'End') {
      nextValue = 92;
    } else {
      return;
    }

    event.preventDefault();
    this.setThermalReveal(nextValue);
  }

  selectThermalHotspot(index: number): void {
    this.selectedThermalHotspotIndex = this.selectedThermalHotspotIndex === index ? null : index;
  }

  trackHotspot(_index: number, hotspot: InspectionCutawayHotspot): string {
    return hotspot.id;
  }

  trackThermalHotspot(_index: number, hotspot: ThermalRevealHotspot): string {
    return hotspot.id;
  }

  trackSampleFinding(_index: number, finding: SampleFinding): string {
    return finding.id;
  }

  onSampleFindingFolderKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      this.nextSampleFinding();
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.previousSampleFinding();
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.selectSampleFinding(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      this.selectSampleFinding(this.sampleFindings.length - 1);
    }
  }

  onCutawayHotspotKeydown(event: KeyboardEvent, index: number): void {
    const keyMap: Record<string, number> = {
      ArrowRight: 1,
      ArrowDown: 1,
      ArrowLeft: -1,
      ArrowUp: -1
    };
    const currentButton = event.currentTarget as HTMLButtonElement | null;
    const buttons = currentButton?.parentElement?.querySelectorAll<HTMLButtonElement>('.cutaway-hotspot');
    let nextIndex = index;

    if (event.key in keyMap) {
      event.preventDefault();
      nextIndex = (index + keyMap[event.key] + this.inspectionCutawayHotspots.length) % this.inspectionCutawayHotspots.length;
    } else if (event.key === 'Home') {
      event.preventDefault();
      nextIndex = 0;
    } else if (event.key === 'End') {
      event.preventDefault();
      nextIndex = this.inspectionCutawayHotspots.length - 1;
    } else {
      return;
    }

    this.selectCutawayHotspot(nextIndex);
    buttons?.item(nextIndex)?.focus();
  }

  selectArea(index: number): void {
    this.selectedAreaIndex = index;
  }

  get selectedArea() {
    return this.areaGuides[this.selectedAreaIndex];
  }

  toggleReadiness(index: number): void {
    this.readinessSteps[index].checked = !this.readinessSteps[index].checked;
  }

  completeReadiness(): void {
    this.readinessSteps.forEach((step) => {
      step.checked = true;
    });
  }

  resetReadiness(): void {
    this.readinessSteps.forEach((step, index) => {
      step.checked = index === 0;
    });
  }

  get completedReadinessCount(): number {
    return this.readinessSteps.filter((step) => step.checked).length;
  }

  get readinessProgress(): number {
    return Math.round((this.completedReadinessCount / this.readinessSteps.length) * 100);
  }

  readinessMarkerPosition(index: number): number {
    if (this.readinessSteps.length <= 1) {
      return 100;
    }

    return Math.round((index / (this.readinessSteps.length - 1)) * 100);
  }

  get readinessMessage(): string {
    if (this.completedReadinessCount === this.readinessSteps.length) {
      return 'The checkup request is ready. Send the request and GTX can follow up with scheduling options.';
    }

    if (this.completedReadinessCount === 0) {
      return 'Start with the property address. GTX can help fill in the rest during follow-up.';
    }

    return 'You can still reach out now. Share the details you have and GTX will help clarify the rest.';
  }

  get nextReadinessTitle(): string {
    const nextStep = this.readinessSteps.find((step) => !step.checked);
    return nextStep ? `Next: ${nextStep.title}` : 'Ready for scheduling';
  }

  selectReport(index: number): void {
    this.selectedReportIndex = index;
  }

  get selectedReportPreview() {
    return this.reportPreviewTabs[this.selectedReportIndex];
  }

  fieldJournalImage(image: string): string {
    return this.fieldJournalImageMap[image] || image;
  }

}
